import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { doc, getDoc, deleteDoc } from "firebase/firestore";
import Spinner from './Spinner.js';
import { ArrowLeft, Package, Warehouse, BarChart3, Clock, DollarSign, Layers, Info, History, Edit2, Copy, Trash2 } from 'lucide-react';

const InventoryDetail = ({ db, user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const docSnap = await getDoc(doc(db, "products", id));
        if (docSnap.exists()) {
          setProduct({ id: docSnap.id, ...docSnap.data() });
        } else {
          alert("Product not found");
          navigate('/ops/inventory');
        }
      } catch (error) {
        console.error("Error fetching inventory detail:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [db, id, navigate]);

  const handleClone = () => {
    navigate('/admin/products/new', { state: { cloneData: product } });
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this product from the master catalog?")) {
      try {
        await deleteDoc(doc(db, "products", id));
        navigate('/admin/products');
      } catch (error) {
        alert("Error: " + error.message);
      }
    }
  };

  if (loading) return <Spinner />;
  if (!product) return null;

  // Placeholder warehouses until schema update
  const warehouseList = ["Jaipur", "Delhi", "Ahmedabad", "Bhiwandi", "Hyderabad"];

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate(-1)} style={backBtnStyle}>
            <ArrowLeft size={24} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{product.name}</h1>
              <span style={skuBadgeStyle}>{product.sku}</span>
            </div>
            <p style={{ color: '#64748b', margin: '4px 0 0 0' }}>{product.description || 'No description available'}</p>
          </div>
        </div>

        {user?.role === 'admin' && (
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={() => navigate(`/admin/products/edit/${id}`)}
              style={{ ...adminBtnStyle, backgroundColor: '#2563eb', color: 'white', border: 'none' }}
            >
              <Edit2 size={18} /> Edit
            </button>
            <button 
              onClick={handleClone}
              style={{ ...adminBtnStyle, backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db' }}
            >
              <Copy size={18} /> Clone
            </button>
            <button 
              onClick={handleDelete}
              style={{ ...adminBtnStyle, backgroundColor: 'white', color: '#ef4444', border: '1px solid #fecaca' }}
            >
              <Trash2 size={18} /> Delete
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '25px' }}>
        {/* LEFT COLUMN */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
          
          {/* SECTION: STOCK SUMMARY MAP (WAREHOUSE BREAKDOWN) */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <Warehouse size={20} color="#2563eb" />
              <h2 style={cardTitleStyle}>Warehouse Distribution</h2>
            </div>
            <div style={{ padding: '20px' }}>
              <table style={detailTableStyle}>
                <thead>
                  <tr style={tableHeaderRowStyle}>
                    <th style={tableThStyle}>Warehouse</th>
                    <th style={tableThStyle}>Saleable</th>
                    <th style={tableThStyle}>Rental</th>
                    <th style={tableThStyle}>Reserved</th>
                    <th style={tableThStyle}>In Repair</th>
                    <th style={tableThStyle}>On Site</th>
                  </tr>
                </thead>
                <tbody>
                  {warehouseList.map(wh => {
                    // This logic will use product.warehouseData[wh] once schema is updated
                    const data = product.warehouseData?.[wh] || {};
                    return (
                      <tr key={wh} style={tableRowStyle}>
                        <td style={{ ...tableTdStyle, fontWeight: '600' }}>{wh}</td>
                        <td style={tableTdStyle}>{data.qtyNew || 0}</td>
                        <td style={tableTdStyle}>{data.qtyRental || 0}</td>
                        <td style={{ ...tableTdStyle, color: '#f59e0b' }}>{data.qtyReserved || 0}</td>
                        <td style={{ ...tableTdStyle, color: '#ef4444' }}>{data.qtyInRepair || 0}</td>
                        <td style={{ ...tableTdStyle, color: '#6366f1' }}>{data.qtyOnSite || 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* SECTION: COMPOSITION (FOR PRESETS) */}
          {product.type === 'preset' && (
            <div style={cardStyle}>
              <div style={cardHeaderStyle}>
                <Layers size={20} color="#6366f1" />
                <h2 style={cardTitleStyle}>Preset Composition (BOM)</h2>
              </div>
              <div style={{ padding: '20px' }}>
                <table style={detailTableStyle}>
                  <thead>
                    <tr style={tableHeaderRowStyle}>
                      <th style={tableThStyle}>SKU</th>
                      <th style={tableThStyle}>Component Name</th>
                      <th style={tableThStyle}>Qty per Set</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.presetComponents?.map((comp, idx) => (
                      <tr key={idx} style={tableRowStyle}>
                        <td style={{ ...tableTdStyle, fontWeight: '600' }}>{comp.sku}</td>
                        <td style={tableTdStyle}>{comp.name || '---'}</td>
                        <td style={tableTdStyle}>{comp.qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SECTION: ACTIVITY LOG (AUDIT TRAIL) */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <History size={20} color="#64748b" />
              <h2 style={cardTitleStyle}>Recent Inventory Activity</h2>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                <Clock size={32} style={{ margin: '0 auto 10px auto', display: 'block', opacity: 0.5 }} />
                No activity records found for this SKU yet.
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
          
          {/* CARD: GLOBAL METRICS */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <BarChart3 size={20} color="#10b981" />
              <h2 style={cardTitleStyle}>Global Metrics</h2>
            </div>
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <MetricItem label="Total Inventory" value={(Number(product.salesQty || 0) + Number(product.rentalQty || 0))} unit={product.uom} />
              <MetricItem label="Available to Move" value={product.availableStock || 0} unit={product.uom} />
              <MetricItem label="Historical Missing" value={product.totalMissing || 0} unit={product.uom} color="#ef4444" />
            </div>
          </div>

          {/* CARD: ATTRIBUTES & CATEGORY */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <Info size={20} color="#2563eb" />
              <h2 style={cardTitleStyle}>Part Attributes</h2>
            </div>
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <AttributeItem label="Item Category" value={product.category || '---'} />
              <AttributeItem label="Type" value={product.type || 'Standard'} />
              <AttributeItem label="Brand" value={product.brand || 'Nexrise'} />
              <AttributeItem label="Manufacturer" value={product.manufacturer || 'Nexrise India'} />
              <AttributeItem label="UOM" value={product.uom || 'nos'} />
              <AttributeItem label="Weight" value={product.weight ? `${product.weight} kg` : '---'} />
            </div>
          </div>

          {/* CARD: FINANCIALS */}
          <div style={cardStyle}>
            <div style={cardHeaderStyle}>
              <DollarSign size={20} color="#f59e0b" />
              <h2 style={cardTitleStyle}>Financial Info</h2>
            </div>
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <MetricItem label="Sales Rate" value={product.rate || 0} prefix="₹" />
              <MetricItem label="Monthly Rent" value={product.rentalRate || 0} prefix="₹" />
              <MetricItem label="Asset Value" value={product.inventoryValue || 0} prefix="₹" />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// --- SUB-COMPONENTS & STYLES ---

const MetricItem = ({ label, value, unit, prefix = '', color = '#111827' }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ fontSize: '13px', color: '#64748b' }}>{label}</span>
    <span style={{ fontSize: '16px', fontWeight: 'bold', color }}>{prefix}{value} {unit}</span>
  </div>
);

const AttributeItem = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ fontSize: '12px', color: '#64748b' }}>{label}</span>
    <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{value}</span>
  </div>
);

const backBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center'
};

const adminBtnStyle = {
  display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px'
};

const skuBadgeStyle = {
  backgroundColor: '#f1f5f9', color: '#475569', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '800', border: '1px solid #e2e8f0'
};

const cardStyle = {
  backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden'
};

const cardHeaderStyle = {
  padding: '15px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#fdfdfd'
};

const cardTitleStyle = {
  fontSize: '15px', fontWeight: '700', margin: 0, color: '#334155'
};

const detailTableStyle = {
  width: '100%', borderCollapse: 'collapse'
};

const tableHeaderRowStyle = {
  borderBottom: '2px solid #f1f5f9'
};

const tableThStyle = {
  textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase'
};

const tableRowStyle = {
  borderBottom: '1px solid #f8fafc'
};

const tableTdStyle = {
  padding: '12px 10px', fontSize: '13px', color: '#334155'
};

export default InventoryDetail;